import type { Spell } from '../../schemas/spell';

/**
 * Spell list **nuovi** del Druido — Tradizione della Natura, livelli L0,
 * L1, L3, L5, L7, L9 (manuale V1, sezione "Lista degli Incantesimi"
 * rr. 6219+, indice Tradizione-→-spell del Druido r. 6200).
 *
 * **Scope**:
 * - 1 orazione L0 nuova (Manifestazione Fatua)
 * - 1 spell L1 nuovo (Ammaliare Animale)
 * - 1 spell L3 nuovo (Evocare Animali)
 * - 2 spell L5 nuovi (Conoscere il Territorio, Zona di Neutralità)
 * - **1 spell L7 nuovo** (D4 — Esplosione Solare)
 * - **1 spell L9 nuovo** (D4 — Piaghe Divine)
 *
 * Gli altri spell della Tradizione della Natura sono **estesi in
 * `mago.ts`/`chierico.ts`** aggiungendo entry `{ classId: 'druido' }` in
 * `availableTo` (pattern additivo, niente refactor degli ID):
 *
 * **L0** (2 estesi):
 * - `mago.luce`
 * - `chierico.purificare`
 *
 * **L1** (5 estesi):
 * - `mago.allarme`
 * - `chierico.curare-ferite-leggere`
 * - `chierico.curare-ferite-moderate` (workaround FI-24)
 * - `chierico.individuare-allineamento`
 * - `mago.invisibilita`
 *
 * **L3** (3 estesi):
 * - `chierico.camminare-con-gli-spiriti`
 * - `chierico.resurrezione`
 * - `mago.sonno`
 *
 * **Spell L5-L9 del Druido**: L5 popolato in D3 (vedi sotto). L7-L9 in D4.
 *
 * **Convenzioni**:
 * - ID `druido.<kebab-name>` per gli spell appartenenti primariamente al
 *   Druido (a differenza di Mago/Chierico, il Druido ha 1 sola Tradizione
 *   "della Natura" — niente discriminante per tradizione in `availableTo`)
 */

// ============================================================================
// Orazioni (Livello 0) — 1 nuovo
// ============================================================================

const manifestazioneFatua: Spell = {
  id: 'druido.manifestazione-fatua',
  name: 'Manifestazione Fatua',
  level: 0,
  tags: [],
  description:
    'Evochi un segno innocuo della presenza degli spiriti della natura. Puoi accendere o spegnere una piccola fiamma, creare un effetto sensoriale naturale che dura solo pochi istanti, oppure far sbocciare un fiore o germogliare un seme.',
  availableTo: [
    { classId: 'druido' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

// ============================================================================
// Livello 1 — 1 nuovo
// ============================================================================

const ammaliareAnimale: Spell = {
  id: 'druido.ammaliare-animale',
  name: 'Ammaliare Animale',
  level: 1,
  tags: ['continuato'],
  description:
    "Tocchi un animale (non una persona o un mostro) e questo ti considera un amico finché non gli dimostri il contrario, non subisce danni o non poni fine all'effetto. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'druido' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
    // Lista del Warlock (manuale r. 6211, L1).
    { classId: 'warlock' },
  ],
};

// ============================================================================
// Livello 3 — 1 nuovo (D3)
// ============================================================================

const evocareAnimali: Spell = {
  id: 'druido.evocare-animali',
  name: 'Evocare Animali',
  level: 3,
  tags: ['continuato'],
  description:
    "Evochi un animale che esegue *senza discutere* i tuoi ordini al meglio delle sue facoltà. Considera l'animale come se fosse un gregario di livello 2, senza alcun Costo. Ha un modificatore di +1 a tutte le Caratteristiche, 1 PF, Carico 6, e infligge 1d6 danni. L'animale ottiene anche 1d4 tratti a tua scelta tra:\n\n• è abile: uno dei modificatori di Caratteristica è +2 anziché +1;\n• è resistente: ha +2 PF per ogni tuo livello da incantatore (al massimo +20 PF);\n• è intelligente e può eseguire compiti complessi;\n• non è spericolato;\n• descrivi una capacità utile che speri che abbia (puoi scegliere quest'opzione fino a due volte).\n\nSulla base dei tratti che hai scelto, il GM ti dirà che animale hai evocato e come si concretizzano le eventuali capacità utili che hai indicato. L'animale resta al tuo fianco finché non muore o non lo congedi. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'druido' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

/** 1 orazione di livello 0 nuova del Druido. */
export const druidoCantrips: readonly Spell[] = [manifestazioneFatua];

/** 1 incantesimo di livello 1 nuovo del Druido. */
export const druidoLevel1Spells: readonly Spell[] = [ammaliareAnimale];

/** 1 incantesimo di livello 3 nuovo del Druido (D3). */
export const druidoLevel3Spells: readonly Spell[] = [evocareAnimali];

// ============================================================================
// Livello 5 — 2 nuovi (D3)
// ============================================================================

const conoscereIlTerritorio: Spell = {
  id: 'druido.conoscere-il-territorio',
  name: 'Conoscere il Territorio',
  level: 5,
  tags: [],
  description:
    "Fondi per un istante la tua coscienza con la natura che ti circonda. Puoi porre al GM fino a tre domande riguardo uno solo di questi argomenti:\n\n• terreno o masse d'acqua;\n• minerali, animali, vegetali o popolazioni rilevanti;\n• creature extraplanari o non morti;\n• influenze da altri piani di esistenza o potenti magie;\n• presenza di edifici o di altre opere artificiali.\n\nLa percezione si limita a qualche chilometro di distanza all'aperto o a qualche centinaio di metri sotto terra. Non puoi percepire ciò che si trova dove la natura è stata sostituita da costruzioni artificiali (come all'interno di un edificio o nel corridoio di una miniera). Le risposte che ricevi sono sempre vere, ma, se usi questo incantesimo più di una volta al giorno, gli spiriti della natura ne saranno infastiditi.",
  availableTo: [
    { classId: 'druido' },
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

const zonaDiNeutralita: Spell = {
  id: 'druido.zona-di-neutralita',
  name: 'Zona di Neutralità',
  level: 5,
  tags: [],
  description:
    "Un'ondata di autorità divina emana da te, imponendo una tregua istantanea: per qualche secondo tutti coloro che possono vederti sono impossibilitati a compiere atti violenti. Tu e i tuoi alleati potrete cogliere l'occasione per negoziare, darvi alla fuga o curare le vostre ferite. Ma ricorda: anche i nemici avranno la stessa opportunità!",
  // Tabelle indice: Druido (Natura) L5 + Chierico-Valore L5. Paladino-Valore
  // eredita da Chierico-Valore; Paladino-Natura(Elfo) eredita da Druido.
  availableTo: [
    { classId: 'druido' },
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

/** 2 incantesimi di livello 5 nuovi del Druido (D3). */
export const druidoLevel5Spells: readonly Spell[] = [conoscereIlTerritorio, zonaDiNeutralita];

// ============================================================================
// Livello 7 — 1 nuovo (D4)
// ============================================================================

const esplosioneSolare: Spell = {
  id: 'druido.esplosione-solare',
  name: 'Esplosione Solare',
  level: 7,
  tags: [],
  description:
    "Dal tuo corpo emana una luce accecante che irradia fino a gittata *vicino*, illuminando a giorno per qualche minuto la zona. Se l'area non è già rischiarata da questo effetto, inoltre, tutti i nemici dotati di vista che si trovano al suo interno al momento del lancio vengono accecati per qualche preziosissimo istante. Infine, gli avversari entro portata *lunga* da te subiscono 2d8 danni con etichette *ignora armatura* e *luminoso*.",
  availableTo: [
    { classId: 'druido' },
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

// ============================================================================
// Livello 9 — 1 nuovo (D4)
// ============================================================================

const piagheDivine: Spell = {
  id: 'druido.piaghe-divine',
  name: 'Piaghe Divine',
  level: 9,
  tags: ['continuato'],
  description:
    "Invochi l'ira della tua divinità su di un luogo che puoi vedere dove vivono delle persone. Descrivi una piaga appropriata alla Sfera d'Influenza del tuo dio: funesterà quel posto finché l'effetto permane. La piaga non uccide istantaneamente, ma consuma le risorse, debilita gli abitanti e diffonde tra di essi il terrore (a discrezione del GM). L'incantesimo termina se decidi di porvi fine, o se usi la mossa *Mana* per ottenere prese. Mentre l'effetto è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'druido' },
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

/** 1 incantesimo di livello 7 nuovo del Druido (D4). */
export const druidoLevel7Spells: readonly Spell[] = [esplosioneSolare];

/** 1 incantesimo di livello 9 nuovo del Druido (D4). */
export const druidoLevel9Spells: readonly Spell[] = [piagheDivine];

/** Tutti gli spell **nuovi** del Druido trascritti (L0 + L1 + L3 + L5 + L7 + L9). */
export const druidoSpells: readonly Spell[] = [
  ...druidoCantrips,
  ...druidoLevel1Spells,
  ...druidoLevel3Spells,
  ...druidoLevel5Spells,
  ...druidoLevel7Spells,
  ...druidoLevel9Spells,
];
